import { CardSubWrapper, CardWrapper } from 'admin/components/wrappers';
import CustomDataGrid from 'app/components/CustomDataGrid/Index';
import React, { useState, useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  CoachInsuranceListWrapper,
  GeneralLookup,
  InsuranceApplicationPdf,
  SendInsurancePdf,
} from 'services/openapi';
import { SortDescriptor } from '@progress/kendo-data-query';
import {
  GridColumnProps,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid';
import { TableSkeletonLoader } from 'app/components/TableSkeletonLoader';
import HorizontalKebabCell from 'app/components/CustomDataGrid/Cells/HorizontalKebabCell';
import { useLocation } from 'react-router-dom';

type CoachInsuranceData = {
  id: string;
  name?: string;
  phone?: string;
  email?: string;
  address?: string;
  team?: string;
};

type Props = {
  setPageSkip: React.Dispatch<React.SetStateAction<number | undefined>>;
  pageSkip: number | undefined;
  pageSize: number;
  debounceShowSearch: string;
  fetchCoachInsuranceList: (
    seasonId?: string,
    searchInput?: string,
    pageNumber?: number,
    pageSize?: number,
  ) => void;
  coachInsuranceData?: CoachInsuranceListWrapper;
  sortField: string | undefined;
  sortOrder: number | undefined;
  setSortField: React.Dispatch<React.SetStateAction<string | undefined>>;
  setSortOrder: React.Dispatch<React.SetStateAction<number | undefined>>;
  setIsOrder: React.Dispatch<React.SetStateAction<boolean>>;
  isOrder: boolean;
  loading: boolean;
  pageSizeSelected: GeneralLookup;
  generatePdf: (
    applicationId: string,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => void;
  getGeneratedPdf: (
    applicationId: string,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => void;
  uploadInsurancePdf: (
    applicationId: string,
    formData?: { file?: Blob },
    onSuccess?: () => void,
  ) => void;
  sendMailtoInsurer: (
    applicationId: string,
    requestBody?: SendInsurancePdf,
    onSuccess?: (data: InsuranceApplicationPdf) => void,
  ) => void;
};
const CoachInsuranceTable = ({
  setPageSkip,
  pageSkip,
  pageSize,
  debounceShowSearch,
  fetchCoachInsuranceList,
  coachInsuranceData,
  sortField,
  sortOrder,
  setSortField,
  setSortOrder,
  setIsOrder,
  isOrder,
  loading,
  pageSizeSelected,
  generatePdf,
  getGeneratedPdf,
  uploadInsurancePdf,
  sendMailtoInsurer,
}: Props) => {
  const initialSort: Array<SortDescriptor> = useMemo(
    () => [{ field: 'name', dir: 'asc' }],
    [],
  );
  const currentLocation = useLocation();
  const [sort, setSort] = React.useState(initialSort);
  const isPastDataFlag = currentLocation.pathname.includes('past-data');
  const [pdfDataMap, setPdfDataMap] = useState<
    Record<string, InsuranceApplicationPdf>
  >({});
  const [selectedAppId, setSelectedAppId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Send to Insurer popup
  const [showSendPopup, setShowSendPopup] = useState(false);
  const [sendPopupAppId, setSendPopupAppId] = useState<string | null>(null);
  const [sendForm, setSendForm] = useState<SendInsurancePdf>({
    to: 'insurer@equisure.com',
    subject: 'YEDA Coach Liability Insurance Application',
    body: 'Please find the attached insurance application.',
  });

  React.useEffect(() => {
    setSort(initialSort);
  }, [pageSize, initialSort]);

  // Seed pdfDataMap from list response so PDF link and Sent date show on initial load
  useEffect(() => {
    const apps = coachInsuranceData?.insuranceApplications;
    if (!apps) return;
    setPdfDataMap(prev => {
      const next = { ...prev };
      apps.forEach(app => {
        if (app.id) {
          next[app.id] = {
            ...next[app.id],
            effectivePdfUrl:
              app.effectivePdfUrl ?? next[app.id]?.effectivePdfUrl,
            sentToInsurerOn:
              app.sentToInsurerOn ?? next[app.id]?.sentToInsurerOn,
          };
        }
      });
      return next;
    });
  }, [coachInsuranceData]);

  const handleMoreClick = (item: string, e: any) => {
    const id: string = e?.dataItem?.id ?? '';
    if (!id) return;

    if (item === 'Generate PDF') {
      generatePdf(id, data => {
        setPdfDataMap(prev => ({ ...prev, [id]: data }));
        fetchCoachInsuranceList('', debounceShowSearch, pageSkip, pageSize);
      });
    } else if (item === 'View / Preview PDF') {
      getGeneratedPdf(id, data => {
        setPdfDataMap(prev => ({ ...prev, [id]: data }));
        const url = data.uploadedPdfUrl ?? data.generatedPdfUrl;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
        }
      });
    } else if (item === 'Upload Edited PDF') {
      setSelectedAppId(id);
      fileInputRef.current?.click();
    } else if (item === 'Send to Insurer') {
      setSendPopupAppId(id);
      setSendForm({
        to: 'insurer@equisure.com',
        subject: 'YEDA Coach Liability Insurance Application',
        body: 'Please find the attached insurance application.',
      });
      setShowSendPopup(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && selectedAppId) {
      uploadInsurancePdf(selectedAppId, { file });
    }
    e.target.value = '';
    setSelectedAppId(null);
  };

  const handleSendConfirm = () => {
    if (!sendPopupAppId) return;
    sendMailtoInsurer(sendPopupAppId, sendForm, data => {
      setPdfDataMap(prev => ({
        ...prev,
        [sendPopupAppId]: { ...prev[sendPopupAppId], ...data },
      }));
    });
    setShowSendPopup(false);
    setSendPopupAppId(null);
  };

  const columns: GridColumnProps[] = [
    {
      field: 'action',
      title: 'Action',
      cell: e => (
        <HorizontalKebabCell
          isAssigned={true}
          e={e}
          onMoreClick={handleMoreClick}
          isCoachInsurance={true}
        />
      ),
      width: `${isPastDataFlag ? '0' : '100'}`,
      sortable: false,
      locked: true,
    },
    {
      field: 'firstName',
      title: 'Name',
      width: '200',
      sortable: true,
      cell: (props: any) => (
        <td>
          {props.dataItem.firstName} {props.dataItem.lastName}
        </td>
      ),
    },
    {
      field: 'primaryPhone',
      title: 'Phone',
      width: '130',
      sortable: false,
    },
    {
      field: 'email',
      title: 'Email',
      width: '220',
      sortable: false,
    },
    {
      field: 'teamLocation',
      title: 'Address',
      width: '250',
      sortable: false,
    },
    {
      field: 'teamName',
      title: 'Team',
      width: '200',
      sortable: true,
    },
    {
      field: 'pdfLink',
      title: 'PDF',
      width: '120',
      sortable: false,
      cell: (props: any) => {
        const rowId = props.dataItem.id;
        const url =
          pdfDataMap[rowId]?.effectivePdfUrl ??
          pdfDataMap[rowId]?.uploadedPdfUrl ??
          pdfDataMap[rowId]?.generatedPdfUrl ??
          props.dataItem.effectivePdfUrl;
        return (
          <td>
            {url ? (
              <ViewPdfBtn href={url} target="_blank" rel="noopener noreferrer">
                View PDF
              </ViewPdfBtn>
            ) : null}
          </td>
        );
      },
    },
    {
      field: 'sentOn',
      title: 'Sent to Insurer',
      width: '160',
      sortable: false,
      cell: (props: any) => {
        const rowId = props.dataItem.id;
        const sentOn =
          pdfDataMap[rowId]?.sentToInsurerOn ?? props.dataItem.sentToInsurerOn;
        return <td>{sentOn ? new Date(sentOn).toLocaleDateString() : null}</td>;
      },
    },
  ];

  const handlePageChange = event => {
    setPageSkip(event.page.skip);
    fetchCoachInsuranceList(
      '',
      debounceShowSearch, // searchInput
      event.page.skip, // pageNumber
      pageSize, // pageSize
    );
  };

  const handleSortChange = e => {
    const sortOrderValue = e.dir === 'asc' ? 0 : 1;
    setSortOrder(sortOrderValue);
    setSortField(e.field);
    fetchCoachInsuranceList(
      '',
      debounceShowSearch, // searchInput
      pageSkip, // pageNumber
      pageSize,
    );
  };

  useEffect(() => {
    if (debounceShowSearch.length !== 0) {
      setPageSkip(1);
      fetchCoachInsuranceList('', debounceShowSearch, (pageSkip = 1), pageSize);
    } else {
      fetchCoachInsuranceList('', debounceShowSearch, pageSkip, pageSize);
    }
  }, [pageSize, pageSizeSelected, debounceShowSearch]);

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Send to Insurer — Gmail-style compose */}
      {showSendPopup && (
        <ComposeOverlay>
          <ComposeWindow>
            <ComposeHeader>
              <ComposeTitle>New Message</ComposeTitle>
              <ComposeClose onClick={() => setShowSendPopup(false)}>
                ✕
              </ComposeClose>
            </ComposeHeader>

            <ComposeRow>
              <ComposeRowLabel>To</ComposeRowLabel>
              <ComposeRowInput
                type="email"
                value={sendForm.to}
                onChange={e =>
                  setSendForm(prev => ({ ...prev, to: e.target.value }))
                }
              />
            </ComposeRow>

            <ComposeRow>
              <ComposeRowLabel>Subject</ComposeRowLabel>
              <ComposeRowInput
                type="text"
                value={sendForm.subject}
                onChange={e =>
                  setSendForm(prev => ({ ...prev, subject: e.target.value }))
                }
              />
            </ComposeRow>

            <ComposeBody
              value={sendForm.body}
              onChange={e =>
                setSendForm(prev => ({ ...prev, body: e.target.value }))
              }
            />

            <ComposeFooter>
              <ComposeSendBtn
                onClick={handleSendConfirm}
                disabled={!sendForm.to || !sendForm.subject || !sendForm.body}
              >
                Send
              </ComposeSendBtn>
            </ComposeFooter>
          </ComposeWindow>
        </ComposeOverlay>
      )}

      {loading ? (
        <TableSkeletonLoader rowContent={8} width={100} headingWidth={80} />
      ) : (
        <CardWrapper style={{ paddingTop: '15px' }}>
          <CardSubWrapper className="d-flex" />
          <GridWrapper>
            <CustomDataGrid
              showPagination={
                (coachInsuranceData?.pageDetails?.count ?? 0) > pageSize
              }
              data={coachInsuranceData?.insuranceApplications ?? []}
              columns={columns}
              pageSize={pageSize}
              totalCount={coachInsuranceData?.pageDetails?.count}
              onPageChange={event => handlePageChange(event)}
              skip={pageSkip}
              sortable={{ allowUnsort: false, mode: 'single' }}
              sort={sort}
              onSortChange={(e: GridSortChangeEvent) => {
                const sortvalue = e.sort[0];
                setSort(e.sort);
                handleSortChange(sortvalue);
              }}
            />
          </GridWrapper>
        </CardWrapper>
      )}
    </>
  );
};

export default CoachInsuranceTable;

const ViewPdfBtn = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 5px 12px;
  font-size: 12px;
  font-weight: 500;
  color: #2961f4;
  background: #eef2ff;
  border: 1px solid #c7d4fd;
  border-radius: 6px;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.15s;
  &:hover {
    background: #dce5ff;
    color: #1a4cc2;
  }
`;

const ComposeOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  z-index: 1000;
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  padding: 0 24px 0 0;
`;

const ComposeWindow = styled.div`
  width: 520px;
  background: #fff;
  border-radius: 10px 10px 0 0;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const ComposeHeader = styled.div`
  background: #404040;
  padding: 10px 16px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ComposeTitle = styled.span`
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: 0.2px;
`;

const ComposeClose = styled.button`
  background: none;
  border: none;
  color: #e0e0e0;
  font-size: 16px;
  cursor: pointer;
  padding: 0 2px;
  line-height: 1;
  &:hover {
    color: #fff;
  }
`;

const ComposeRow = styled.div`
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e5e7eb;
  padding: 0 16px;
`;

const ComposeRowLabel = styled.span`
  font-size: 13px;
  color: #5f6368;
  min-width: 52px;
  flex-shrink: 0;
`;

const ComposeRowInput = styled.input`
  flex: 1;
  border: none;
  outline: none;
  font-size: 14px;
  color: #202124;
  padding: 10px 8px;
  background: transparent;
`;

const ComposeBody = styled.textarea`
  flex: 1;
  border: none;
  outline: none;
  resize: none;
  font-size: 14px;
  color: #202124;
  padding: 12px 16px;
  min-height: 200px;
  font-family: inherit;
  background: #fff;
`;

const ComposeFooter = styled.div`
  padding: 10px 16px 14px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ComposeSendBtn = styled.button`
  background: #1a73e8;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 8px 22px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 0.3px;
  &:hover:not(:disabled) {
    background: #1765cc;
  }
  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

const GridWrapper = styled.div`
  .k-grid-container {
    height: calc(100vh - 285px);
  }
  .k-grid-content tr {
    td {
      height: 60px !important;
      padding: 12px 16px;
    }
  }
  .k-grid-content tr {
    &:hover {
      td {
        background: #f0f0f0 !important;
      }
    }
  }
  .k-grid-content tr {
    td {
      &.k-grid-content-sticky {
        cursor: default;
      }
    }
    .show-name-style {
      cursor: pointer !important;
      .name-label {
        cursor: pointer !important;
      }
    }
  }
  .k-table-thead {
    .k-link {
      cursor: default;
    }
    .show-header-style,
    .show-date-header-style {
      cursor: pointer !important;
      .k-link {
        cursor: pointer !important;
      }
    }
  }
`;
